
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import WindowStepper from '@/components/WindowStepper.vue';
import RunnitsSubscriptionCard from '@/views/Runnits/pages/RunnitsSubscriptionsPage/RunnitsSubscriptionCard.vue';
import RunnitsSubscriptionCardComingSoon from '@/views/Runnits/pages/RunnitsSubscriptionsPage/RunnitsSubscriptionCardComingSoon.vue';
import {
	CHECKOUT_USER_SUBSCRIPTION,
	CLUB_KEY,
	CLUB_OFFER_TYPE,
	ClubOffer,
	currencyTokens,
	hundredthsToTokens,
} from '@run-diffusion/shared';
import { deriveTokenBuckets, TokenBuckets } from '@/utils/tokenBuckets';
import _clamp from 'lodash/clamp';
import _omit from 'lodash/omit';
import _isNil from 'lodash/isNil';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import IsMonthlySwitch from '@/views/Runnits/pages/RunnitsSubscriptionsPage/IsMonthlySwitch.vue';
import TokenCalculationsTable from '@/views/Runnits/pages/RunnitsSubscriptionsPage/TokenCalculationsTable.vue';
import PersonalSubscriptionManagement
	from '@/views/Runnits/pages/RunnitsSubscriptionsPage/PersonalSubscriptionManagement.vue';
import RunnitFaqs from '@/views/Runnits/base/RunnitFaqs.vue';
import { ROUTER } from '@/router/constants';
import ListItem from '@/components/base/ListItem.vue';
import RunDiffusionWithRunnitMarkLogo from '@/assets/RunDiffusionWithRunnitMarkLogo.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';
import { analytics, EVENTS } from '@/services/analytics';

export default Vue.extend({
	name: 'RunnitsSubscriptionsPage',
	metaInfo: {
		title: 'Subscriptions',
	},
	data () {
		return {
			CLUB_KEY,
			carouselModel: 0,
			// Set once on load: mobile carousel opens on the user's current plan, not the first card
			didInitCarouselToCurrentPlan: false,
			goToManageSubscriptionDialogOpen: false,
			// Only the "Downgrade Plan" action on the Free Plan card shows the downgrade consequences;
			// the "Manage" button (payment method / portal) opens the same dialog without the warning.
			showDowngradeToFreeWarning: false,
			selectedTokenClubAddOnsMap: {}, // Map from ClubKey to TokenClubAddOn obj
			selectedLimitClubAddOnsMap: {}, // Map from ClubKey to LimitClubAddOn obj
		};
	},
	created () {
		this.bindClubOffers();

		// Top of the conversion funnel — reached the paywall/pricing surface.
		analytics.track(EVENTS.PRICING_PAGE_VIEWED, {
			current_plan: this._get(this.user, 'club', null),
			entry_source: this._get(this.$route, 'query.source', null),
		});
	},
	watch: {
		visibleUserClubOffers: {
			immediate: true,
			handler (offers: ClubOffer[]) {
				// Once the offers load, open the mobile carousel on the user's current plan (not 1/4).
				// Guarded so it only seeds the initial position and never fights manual navigation.
				if (this.didInitCarouselToCurrentPlan || !offers || !offers.length) return;
				const currentClub: string = this._get(this.user, 'club') || null;
				const idx: number = offers.findIndex((clubOffer: ClubOffer) => clubOffer.club === currentClub);
				if (idx >= 0) {
					this.carouselModel = idx;
				}
				this.didInitCarouselToCurrentPlan = true;
			},
		},
	},
	computed: {
		...mapState([
			'authUser',
			'user',
			'clubOffers',
			'clubOffersMap',
			'loadingClubOffers',
			'userBalanceAccount',
		]),
		...mapGetters([
			'userClubOfferBenefits',
			'displayLabels',
			'activeDomainClaimRestriction',
		]),
		/*
		Tokens the user actually paid cash for (this cycle's top-ups plus any that rolled over).
		Downgrading to the free plan zeroes the whole paid balance, so these die with it — worth
		naming the number rather than letting "your tokens will expire" cover it silently.
		 */
		expiringTopUpTokens (): number {
			if (!this.userBalanceAccount) return 0;
			const buckets: TokenBuckets = deriveTokenBuckets(this.userBalanceAccount);
			return Math.max((buckets.topUp || 0) + (buckets.rolledOverTopUp || 0), 0);
		},
		expiringTopUpTokensDisplay (): string {
			return currencyTokens(hundredthsToTokens(this.expiringTopUpTokens));
		},
		isLoading () {
			return this.loadingClubOffers === null || this.loadingClubOffers;
		},
		hasActiveNonFreemiumSubscription () {
			return !!(
				this.user &&
				this.user.clubOffer &&
				typeof this.user.clubOffer !== 'string' &&
				!this.user.clubOffer.isFreemium
			);
		},
		showCcOptions () {
			return !!this.$route.query.showCcOptions;
		},
		/*
		Three states, not two. `isMonthly=1` and `isMonthly=0` are explicit choices the user made on
		this page; an ABSENT param means they have not chosen yet, and we default from their actual
		subscription — monthly sub opens on monthly, everything else (annual sub, free, signed out)
		opens on yearly.

		This is derived rather than written on load on purpose: the previous version had a watcher in
		IsMonthlySwitch race to push `isMonthly=1` into the URL, which silently lost whenever the
		component mounted before user.clubOffer resolved (it arrives as a string ref first) or the
		router replace was cancelled. As a computed it just re-evaluates when the user hydrates.
		 */
		isMonthly (): boolean {
			const explicit: string = this.$route.query.isMonthly;
			if (!_isNil(explicit)) return explicit === '1';
			if (!this.hasActiveNonFreemiumSubscription) return false;
			return !this._get(this.user, 'clubInfo.isAnnual');
		},
		isCc (): boolean {
			return !!(this.userClubOfferBenefits && this.userClubOfferBenefits.isCc);
		},
		visibleUserClubOffers () {
			return (this.clubOffers || []).filter((clubOffer: ClubOffer) => {
				return !!(
					clubOffer.type === CLUB_OFFER_TYPE.USER &&
					(
						this.isCc || // Their current subscription is an `isCc` subscription
						this.showCcOptions ||
						!clubOffer.benefits.isCc
					)
				);
			});
		},
		carouselLength () {
			return (
				this.visibleUserClubOffers.length +
				1 // Enterprise coming soon selection
				// 1 Team coming soon selection
			);
		},
	},
	methods: {
		...mapActions([
			'bindClubOffers',
			'goToStripeSubscriptionPortal',
			'updateSnackbar',
		]),
		goToLogin () {
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.LOGIN,
				query: {
					...this.$route.query,
					redirect: this.$route.path,
				},
			});
		},
		onChangeShowCcOptions (val: boolean) {
			this.routerReplace(this.$route, this.$router, {
				name: this.$route.name,
				params: this.$route.params,
				query: {
					..._omit(this.$route.query, 'showCcOptions'),
					...(val && {
						showCcOptions: '1',
					}),
				},
			});
		},
		onChangeIsMonthly (val: boolean) {
			this.routerReplace(this.$route, this.$router, {
				name: this.$route.name,
				params: this.$route.params,
				query: {
					..._omit(this.$route.query, 'isMonthly'),
					// Always write a value, including for yearly: an absent param is reserved for
					// "no choice yet" so isMonthly can default from the user's subscription
					isMonthly: val ? '1' : '0',
				},
			});
		},
		scrollToPlans () {
			const el: HTMLElement = this.$refs.plansSection as HTMLElement;
			if (!el) return;
			this.$scrollTo(el, 500, {
				offset: 0,
			});
		},
		handleCtaClick ({ clubOffer, tokenClubAddOn, limitClubAddOn }) {
			if (this.activeDomainClaimRestriction && !clubOffer.isFreemium) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team),
					show: true,
				});
				return;
			}
			const routeDataGoToPayment = this.$router.resolve({
				name: 'GoToPayment',
				query: {
					checkoutAction: CHECKOUT_USER_SUBSCRIPTION,
					club: clubOffer.club,
					...(!this.isMonthly && {
						annual: '1',
					}),
					tokenClubAddOnId: tokenClubAddOn.id,
					limitClubAddOnId: limitClubAddOn.id,
				},
			});

			if (!this.user && clubOffer.isFreemium) {
				// Unauthenticated user select free plan
				this.routerPush(this.$route, this.$router, {
					name: ROUTER.SIGNUP,
					query: this.$route.query,
				});
			} else if (this.user && clubOffer.isFreemium) {
				// Logged-in user select free plan (downgrade) — show the downgrade consequences
				this.showDowngradeToFreeWarning = true;
				this.goToManageSubscriptionDialogOpen = true;
			} else if (!this.user && !clubOffer.isFreemium) {
				// Unauthenticated user select paid plan
				const routeDataSignup = this.$router.resolve({
					name: ROUTER.SIGNUP,
					query: {
						...this.$route.query,
						redirect: routeDataGoToPayment.href,
					},
				});
				window.open(routeDataSignup.href, '_blank');
			} else if (this.user && !clubOffer.isFreemium) {
				// Logged-in user select paid plan
				window.open(routeDataGoToPayment.href, '_blank');
			}
		},
		handleStepClick (stepIndex: number) {
			this.carouselModel = stepIndex;
		},
		prevSlide () {
			if (this.carouselModel > 0) this.carouselModel--;
			this.carouselModel = _clamp(this.carouselModel, 0, this.carouselLength - 1);
		},
		nextSlide () {
			if (this.carouselModel < this.carouselLength - 1) this.carouselModel++;
			this.carouselModel = _clamp(this.carouselModel, 0, this.carouselLength - 1);
		},
	},
	components: {
		RunDiffusionWithRunnitMarkLogo,
		ListItem,
		RunnitFaqs,
		PersonalSubscriptionManagement,
		TokenCalculationsTable,
		IsMonthlySwitch,
		ConfirmDialog,
		WindowStepper,
		RunnitsSubscriptionCard,
		RunnitsSubscriptionCardComingSoon,
	},
});
